import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

// Set base URL once — not inside render
// In production, REACT_APP_API_URL must point to the Render backend URL
// In development, empty string uses CRA proxy (localhost:5003)
const API_URL = process.env.REACT_APP_API_URL || '';

axios.defaults.baseURL = API_URL;

// Helper: set or clear the auth header globally
const setAuthHeader = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('patrol_token'));
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('patrol_user')); }
    catch { return null; }
  });

  // On mount: restore auth header from stored token
  useEffect(() => {
    const stored = localStorage.getItem('patrol_token');
    if (stored) {
      setAuthHeader(stored);
      // Verify token is still valid by hitting a protected endpoint
      axios.get('/api/auth/me').catch(() => {
        // Token invalid — clear everything so user re-logs in
        localStorage.removeItem('patrol_token');
        localStorage.removeItem('patrol_user');
        setAuthHeader(null);
        setToken(null);
        setUser(null);
      });
    }
  }, []);

  const login = async (email, password) => {
    const res = await axios.post('/api/auth/login', { email, password });
    const t = res.data.accessToken || res.data.token;
    const u = res.data.user;
    if (!t) throw new Error('No token received from server');
    localStorage.setItem('patrol_token', t);
    localStorage.setItem('patrol_user', JSON.stringify(u));
    setAuthHeader(t);
    setToken(t);
    setUser(u);
    return u;
  };

  const logout = () => {
    localStorage.removeItem('patrol_token');
    localStorage.removeItem('patrol_user');
    setAuthHeader(null);
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ token, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
